import Stat from "../../../components/Stat";
import { ToggletipText, ToggletipTextWrapper } from "../../../style";
import useScreenBreakpoint from "../../../hooks/useScreenBreakpoint";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  hasLateActivitiesCounterAccess,
  hasLawsuitUpdatesCounterCaseTypeFilterAccess,
} from "../../../selectors/powersSelectors";
import { isStarter } from "../../../selectors/userInfoSelectors";
import { sendEventChartUsed } from "../../../services/dashboardAnalyticEventService";
import { dashboardChartFilterAndActionTypes } from "../../../utils/constants/dashboard/analyticDashboardEvents";
import {
  DASHBOARD_CASE_TYPES,
  DASHBOARD_CHART_TYPES,
  DASHBOARD_RANGE_TYPE_KEYS,
  DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES,
} from "../../../utils/constants/dashboard/dashboardModuleConstants";
import { lawsuitsAndCasesLocalStorageConstants } from "../../../utils/constants/localStorageConstants";
import { save as saveOnLocalStorage } from "../../../services/localStorageService";

import DashboardChartFilters from "../../../components/DashboardChartFilters";
import DashboardChartCaseTypeFilter from "../../../components/DashboardChartFilters/DashboardChartCaseTypeFilter";
import DashboardChartRangeFilter from "../../../components/DashboardChartFilters/DashboardChartRangeFilter";
import DashboardChartWrapper from "../../../components/DashboardChartWrapper";
import { QueryLoaderWrapper, Skeletons } from "../../../components/Loaders";
import { CardWrapper, CounterContainer } from "./style";
import useLawsuitUpdatesCounterQuery from "./useLawsuitUpdatesCounterQuery";
import ChartActions from "@components/ChartActions/ChartActions";

const { LAWSUITS_AND_CASES_MOVEMENTS } = lawsuitsAndCasesLocalStorageConstants;

const LawsuitUpdatesCounter = () => {
  const ALLOWED_RANGE_TYPES: DASHBOARD_RANGE_TYPE_KEYS[] = [
    "TODAY",
    "LAST_7_DAYS",
    "LAST_30_DAYS",
    "LAST_60_DAYS",
    "LAST_90_DAYS",
  ];

  const [caseTypeFilter, setCaseTypeFilter] = useState<{ queryParam: string[]; label: string }>(
    DASHBOARD_CASE_TYPES.ALL
  );
  const [rangeFilter, setRangeFilter] =
    useState<DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES>("LAST_30_DAYS");

  const shouldRenderCaseTypesFilter = useSelector(hasLawsuitUpdatesCounterCaseTypeFilterAccess);
  const isLateActivitiesCounterRendered = useSelector(hasLateActivitiesCounterAccess);
  const [status, lawsuitUpdatesCount, fetchedTime, refetch] = useLawsuitUpdatesCounterQuery(
    caseTypeFilter.queryParam,
    rangeFilter
  );

  const { isLargeScreen } = useScreenBreakpoint();
  const isUpOrSmartPlan = useSelector(isStarter);

  const shouldRenderTooltipAtLeft = isLargeScreen && isLateActivitiesCounterRendered;

  const setRangeFilterAndSendMixPanelEvent = (filter: DATE_RANGE_DEFAULT_HISTOGRAM_KEY_TYPES) => {
    setRangeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_COUNTER,
      dashboardChartFilterAndActionTypes.DATE_RANGE,
      filter
    );
  };

  const setCaseTypeFilterAndSendMixPanelEvent = (filter: {
    queryParam: string[];
    label: string;
  }) => {
    setCaseTypeFilter(filter);
    sendEventChartUsed(
      DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_COUNTER,
      dashboardChartFilterAndActionTypes.LAWSUIT_TYPE,
      filter
    );
  };

  const setLawsuitsUpdatesFiltersRedirect = (withMovements = false) => {
    saveOnLocalStorage(
      LAWSUITS_AND_CASES_MOVEMENTS,
      JSON.stringify({
        movements: withMovements,
        dateRange: rangeFilter,
        caseType: caseTypeFilter.queryParam,
      })
    );
    window.open("/#/main/folders/%5B,,%5D", "_self");
  };

  const dashboardsFiltersRedirectProps =
    (withMovements = false) =>
    () =>
      setLawsuitsUpdatesFiltersRedirect(withMovements);

  const header = {
    title: "Movimentação de processos e casos do escritório",
    description: isUpOrSmartPlan
      ? "Confira a quantidade de processos e casos que possuem ou não movimentações no período selecionado."
      : "Confira a quantidade de processos e casos que possuem ou não movimentações no período selecionado para observar se há novidades ou situações atípicas.",
  };

  const toggleTip = {
    title: "Movimentação de processos e casos",
    text: (
      <ToggletipTextWrapper>
        <ToggletipText>
          Movimentação é qualquer histórico vinculado ao processo ou caso do Astrea, tendo origem no
          tribunal ou não.
        </ToggletipText>
        {isUpOrSmartPlan && (
          <ToggletipText>
            No indicador, as quantidades de processos e casos são somadas.
          </ToggletipText>
        )}
      </ToggletipTextWrapper>
    ),
    alwaysOnLeft: shouldRenderTooltipAtLeft,
    onClickCallback: () => {
      sendEventChartUsed(
        DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_COUNTER,
        dashboardChartFilterAndActionTypes.TOGGLE_TIP_OPENED
      );
    },
    callToAction: {
      text: "Acessar central de ajuda",
      callback: () => {
        sendEventChartUsed(
          DASHBOARD_CHART_TYPES.LAWSUIT_UPDATES_COUNTER,
          dashboardChartFilterAndActionTypes.HELP_CENTER_CLICK
        );
        window.open(
          "https://astrea.aurum.com.br/pt-BR/articles/8043376-como-analisar-quantos-processos-e-casos-tiveram-movimentacao",
          "_blank"
        );
      },
    },
  };

  return (
    <DashboardChartWrapper
      title={header.title}
      description={header.description}
      toggleTip={toggleTip}
    >
      <ChartActions updatedAt={fetchedTime} refetchFunction={refetch} />
      <CardWrapper>
        <DashboardChartFilters>
          <DashboardChartRangeFilter
            allowedRangeTypes={ALLOWED_RANGE_TYPES}
            selectedRange={rangeFilter}
            setRangeFunction={setRangeFilterAndSendMixPanelEvent}
          />
          {shouldRenderCaseTypesFilter && (
            <DashboardChartCaseTypeFilter
              currentFilter={caseTypeFilter}
              setFilter={setCaseTypeFilterAndSendMixPanelEvent}
            />
          )}
        </DashboardChartFilters>
        <QueryLoaderWrapper
          queryStatus={status}
          spinnerConfig={{ height: 56.8, isCounter: true }}
          skeletonComponent={<Skeletons.LawsuitUpdateCounterSkeleton />}
        >
          <CounterContainer>
            <Stat
              label="Com movimentação"
              value={lawsuitUpdatesCount?.hasUpdates}
              onClick={dashboardsFiltersRedirectProps(true)}
            />
            <Stat
              label="Sem movimentação"
              value={lawsuitUpdatesCount?.noUpdates}
              onClick={dashboardsFiltersRedirectProps()}
            />
          </CounterContainer>
        </QueryLoaderWrapper>
      </CardWrapper>
    </DashboardChartWrapper>
  );
};

export default LawsuitUpdatesCounter;
